/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {DestroyRef, inject, Component, Inject, signal} from '@angular/core';
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import {SignalResourceListBindingService} from '../../common/controller/signal-resource-list-binding.service';
import * as _ from 'lodash';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import template from './marketplace-product-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {LayoutSelectorModalComponent} from '../product/layout-selector-modal.component';
import {TeamApiService} from '../team/team-api.service';
import {LayoutApiService} from '../layout/layout-api.service';
import {LineItemApiService} from '../../line-item/line-item-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {compareById} from '../../common/util/object-id-comparison';
import {MarketplaceProductApiService, MarketplaceProductModel} from './marketplace-product-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {AdminResourcePagesModule} from '../../common/controller/admin-resource-pages.module';
import {PercentageBarComponent} from '../../common/controller/progress-bar-percentage-component';
import {VelocityComponent} from '../../common/controller/velocity-component';
import {RateLimitComponent} from '../../common/controller/rate-limit.component';
import {AdminListModule} from '../../common/directives/admin-list.module';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';
import {LayoutComponentSummaryComponent} from '../../common/layout-component-summary-component';
import {TargetingModule} from '../../targeting/targeting.module';

function allowedDims(theModel: any): any[] {
  return _.filter(theModel.allowedDimensions, function(dims) {
    return dims[0] >= 0 && dims[1] >= 0;
  });
}

function normalizeResourceIds(items: any[]): string[] {
  return _.compact(_.map(items || [], (item: any) => _.isString(item) ? item : item.id));
}

function ownerTeamId(model: any): string | undefined {
  const ownerTeam = _.get(model, 'ownerTeam');
  return _.isString(ownerTeam) ? ownerTeam : _.get(ownerTeam, 'id');
}

@Component({
  standalone: true,
  imports: [
    AppSharedModule,
    FormsModule,
    TranslateModule,
    UIRouterModule,
    ButtonsModule,
    AdminListModule,
    AdminResourcePagesModule,
    PercentageBarComponent,
    VelocityComponent,
    RateLimitComponent,
    FormControlsModule,
    LayoutComponentSummaryComponent,
    LayoutSelectorModalComponent,
    TargetingModule
  ],
  selector: 'adn-marketplace-product-edit-page',
  template
})
export class MarketplaceProductEditPageComponent extends SignalAdminResourceEditPageBase<MarketplaceProductModel> {
  private readonly listLifetime = inject(DestroyRef);
  private readonly allTeamsState = signal<any[]>([]);
  private readonly allPublisherTeamsState = signal<any[]>([]);
  private readonly hasLayoutsState = signal(false);
  private readonly layoutResultState = signal<any>({});
  private readonly targetingResultState = signal<any>({});

  get allTeams(): any[] { return this.allTeamsState(); }
  set allTeams(value: any[]) { this.allTeamsState.set(value); }
  get allPublisherTeams(): any[] { return this.allPublisherTeamsState(); }
  set allPublisherTeams(value: any[]) { this.allPublisherTeamsState.set(value); }
  get hasLayouts(): boolean { return this.hasLayoutsState(); }
  set hasLayouts(value: boolean) { this.hasLayoutsState.set(value); }
  get layoutResult(): any { return this.layoutResultState(); }
  set layoutResult(value: any) { this.layoutResultState.set(value); }
  get targetingResult(): any { return this.targetingResultState(); }
  set targetingResult(value: any) { this.targetingResultState.set(value); }
  canUpdateProduct = false;
  dynamicBiddingAllowed = false;
  lineItemColumns = ['name', 'order', 'userState', 'executionState'];
  lineItemProducts: any;
  get displayLineItems(): any[] { return this.lineItemProducts?.listState?.items() || []; }
  get hasLineItems(): boolean { return this.displayLineItems.length > 0; }
  get listPageSize(): number { return this.lineItemProducts?.listState?.pageSize || 25; }
  get listTotalCount(): number { return this.lineItemProducts?.listState?.totalCount() || 0; }
  get listCurrentPage(): number { return this.lineItemProducts?.listState?.currentPage() || 1; }
  ownership = 'NETWORK';
  rateLimitTypes: string[] = [];
  selSmoothingOption = '';
  showOwnership = false;
  showPolitical = false;
  teamsWarning = false;
  tiers: any[] = [];
  targetingTypes = _.map(ADN_TARGETING_TYPES, function(val) {
    return val;
  });

  readonly smoothingOptions = [
    {id: 'optionEf', vals: ['EVEN', 'FRONT_LOADED']},
    {id: 'optionEfs', vals: []},
    {id: 'optionEfcc', vals: ['EVEN', 'FRONT_LOADED', 'CAPPED', 'CATCH_UP']},
    {id: 'optionEfccg', vals: ['EVEN', 'FRONT_LOADED', 'CAPPED', 'CATCH_UP', 'GREEDY']},
    {id: 'optionEfcg', vals: ['EVEN', 'FRONT_LOADED', 'CATCH_UP', 'GREEDY']},
    {id: 'optionUef', vals: ['FAST_AS_POSSIBLE', 'EVEN', 'FRONT_LOADED']},
    {id: 'optionUefs', vals: ['FAST_AS_POSSIBLE', 'EVEN', 'FRONT_LOADED']},
    {id: 'optionE', vals: ['EVEN']},
    {id: 'optionC', vals: ['CAPPED']},
    {id: 'optionF', vals: ['FAST_AS_POSSIBLE']}
  ];

  private readonly defaultSmoothingOption = {id: 'optionEfs', vals: []};

  constructor(
    @Inject(LocalNetworkProfile) private readonly marketplaceNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(LineItemApiService) private LineItem: LineItemApiService,
    private readonly marketplaceProductApi: MarketplaceProductApiService,
    private readonly teamApi: TeamApiService,
    @Inject(LayoutApiService) private Layout: LayoutApiService,
    private listBinding: SignalResourceListBindingService,
    private angularModalService: AngularModalService,
    private targetingEditorModalService: TargetingEditorModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(marketplaceProductApi, 'marketplaceProduct');

    this.rateLimitTypes = ['RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'BUDGET'];
    if (this.marketplaceNetworkProfile.impsOnAxProductRateLimits()) {
      this.rateLimitTypes.unshift('IMPRESSION');
    }

    this.dynamicBiddingAllowed = this.marketplaceNetworkProfile.isMarketplacePlatform();
    this.showPolitical = this.marketplaceNetworkProfile.isPureMarketplacePlatform();
    // note we can't check AX_PUBLISHER_TEAM_ADMIN because we don't know the ownerTeam as yet so it is checked
    // in updateCanUpdateProduct
    this.canUpdateProduct = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.showOwnership = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');

    this.teamApi.listOptions({minimal: true, filterBy: 'type', filterByValue: 'AX_ADVERTISER'}).subscribe((page) => {
      this.allTeams = page.results;
    });

    this.teamApi.listOptions({minimal: true, filterBy: 'type', filterByValue: 'AX_PUBLISHER'}).subscribe((page) => {
      this.allPublisherTeams = page.results;
      this.resolveOwnerTeamReference();
    });
  }

  get lineItemsResolved(): any {
    return this.isNew || !this.lineItemProducts?.listState?.loading();
  }

  get lineItemPageNumbers(): number[] {
    return visiblePageNumbers(this.listCurrentPage, this.lineItemTotalPages());
  }

  protected override afterEditingModelReset(): void {
    this.updateCanUpdateProduct();
    this.resolveOwnerTeamReference();
    this.hasLayouts = false;
    if (this.editingModel.layouts && this.editingModel.layouts.length) {
      firstValueFrom(this.Layout.listOptions(resourceOptionParams(_.map(this.editingModel.layouts, 'id')))).then((page: any) => {
        // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferSignalStateWrite(() => {
          this.editingModel = {...this.editingModel, layouts: page.results};
          this.hasLayouts = true;
        });
      });
    } else {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.hasLayouts = true;
      });
    }

    this.editingModel.layouts = this.editingModel.layouts || [];
    this.editingModel.type = this.editingModel.type || 'STANDARD';
    this.editingModel.cpmSpecification = this.editingModel.cpmSpecification || 'UNCAPPED';
    this.editingModel.budgetSpecification = this.editingModel.budgetSpecification || 'ALL_OBJECTIVES';
    this.editingModel.lineItemWorkflowType = this.editingModel.lineItemWorkflowType || 'DEFAULT';
    this.editingModel.objectiveSpecification = this.editingModel.objectiveSpecification || 'ANY';
    this.editingModel.bidStrategyOption = this.editingModel.bidStrategyOption || 'STANDARD_ONLY';
    this.editingModel.teams = normalizeResourceIds(this.editingModel.teams);
    this.editingModel.allowedDimensions = _.isArray(this.editingModel.allowedDimensions) ? this.editingModel.allowedDimensions : [[]];
    this.editingModel.allowedDimensions[0] = _.isArray(this.editingModel.allowedDimensions[0]) ? this.editingModel.allowedDimensions[0] : [];
    this.editingModel.targeting = this.editingModel.targeting || {};
    this.editingModel.optionalTargetTypes = this.editingModel.optionalTargetTypes || [];

    const enabledTiers = this.marketplaceNetworkProfile.get('tiers') || [];
    const currentTier = this.editingModel.defaultTier;
    this.tiers = _.cloneDeep(enabledTiers);
    if (currentTier && !_.find(this.tiers, (tier: any) => tier.id === _.get(currentTier, 'id'))) {
      this.tiers.push(currentTier);
    }
    _.forEach(this.tiers, (tier: any) => {
      tier.displayName = tier.name + (_.find(enabledTiers, {id: tier.id}) ? '' : ' (disabled)');
    });
    this.runAfterEditingModelReset();
  }

  protected override afterSave(data: MarketplaceProductModel): void {
    this.teamsWarning = (this.editingModel.teams || []).length !== (data.teams || []).length;
  }

  private runAfterEditingModelReset(): void {
    this.ownership = this.editingModel.ownerTeam ? 'SPECIFIC_TEAM' : 'NETWORK';
    this.resolveOwnerTeamReference();
    this.editingModel.teams = normalizeResourceIds(this.editingModel.teams || []);
    this.updateCanUpdateProduct();
    this.selSmoothingOption = (_.find(this.smoothingOptions, (option) => {
      return option.vals.join(', ') === (this.editingModel.smoothingChoices || []).join(', ');
    }) || this.defaultSmoothingOption).id;
    this.targetingResult = {data: false};
    this.layoutResult = {data: false};

    if (!this.isNew && !this.lineItemProducts) {
      this.lineItemProducts = this.listBinding.create(this.LineItem, '', this.listLifetime, {
        baseTable: 'lineItem',
        filterBy: 'executionState',
        filterByValue: 'RUNNING',
        moreParams: {
          axProduct: this.editingModel.id
        }
      });
    }
  }

  protected override prepareForSubmit(model: MarketplaceProductModel): void {
    model.allowedDimensions = allowedDims(model);
    if (this.ownership === 'NETWORK') {
      model.ownerTeam = null;
    }
    if (this.isNew) {
      this.localNetworkInfo.increment('axProductsCount');
    }
    model.smoothingChoices = (_.find(this.smoothingOptions, (option) => {
      return option.id === this.selSmoothingOption;
    }) || this.defaultSmoothingOption).vals;
  }

  editsMade(): void {
    this.markExternalFormDirty();
  }

  addDimensions(): void {
    this.editingModel.allowedDimensions = this.editingModel.allowedDimensions || [];
    this.editingModel.allowedDimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.allowedDimensions, index);
    if (this.editingModel.allowedDimensions.length < 1) {
      this.editingModel.allowedDimensions[0] = [];
    }
    this.markExternalFormDirty();
  }

  editTarget(targeting: any): void {
    this.targetingResult = {data: false};
    this.targetingEditorModalService.open({
      modelLineItem: _.cloneDeep(this.editingModel.data || {}),
      lineItemType: this.editingModel.type === 'POLITICAL' ? 'POLITICAL' : 'NORMAL',
      pageType: 'marketplaceProduct',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: null,
      overridableTargeting: undefined,
      targetingMode: null,
      targeting,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      this.editingModel.targeting = _.cloneDeep(targeting);
      this.model.targeting = _.cloneDeep(targeting);
      if (!this.isNew) {
        firstValueFrom(this.marketplaceProductApi.savePartial(this.editingModel, 'targeting')).then((respObj: any) => {
          this.targetingResult = {...this.targetingResult, data: true};
          this.editingModel = {...this.editingModel, validationWarnings: respObj.validationWarnings};
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  toggleTeam(teamId: string): void {
    this.editingModel.teams = this.editingModel.teams || [];
    const idx = this.editingModel.teams.indexOf(teamId);
    if (idx > -1) {
      this.editingModel.teams.splice(idx, 1);
    } else {
      this.editingModel.teams.push(teamId);
    }
    this.markExternalFormDirty();
  }

  hasTeam(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleOptionalTargetType(targetType: string): void {
    this.editingModel.optionalTargetTypes = this.editingModel.optionalTargetTypes || [];
    const idx = this.editingModel.optionalTargetTypes.indexOf(targetType);
    if (idx > -1) {
      this.editingModel.optionalTargetTypes.splice(idx, 1);
    } else {
      this.editingModel.optionalTargetTypes.push(targetType);
    }
    this.markExternalFormDirty();
  }

  hasOptionalTargetType(targetType: string): boolean {
    return (this.editingModel.optionalTargetTypes || []).indexOf(targetType) > -1;
  }

  editLayouts(): void {
    this.layoutResult = {data: false};
    const modalRef = this.angularModalService.show(LayoutSelectorModalComponent, {
      class: 'modal-lg',
      initialState: {currentLayouts: _.cloneDeep(this.editingModel.layouts)}
    });
    (modalRef.content as LayoutSelectorModalComponent).onSaved.subscribe((layouts: any[]) => {
      this.editingModel = {...this.editingModel, layouts: _.cloneDeep(layouts)};
      this.model.layouts = _.cloneDeep(layouts);
      this.markExternalFormDirty();
      if (!this.isNew) {
        firstValueFrom(this.marketplaceProductApi.savePartial(this.editingModel, 'layouts')).then((respObj: any) => {
          this.layoutResult = {...this.layoutResult, data: true};
          this.editingModel = {...this.editingModel, validationWarnings: respObj.validationWarnings};
        });
      }
    });
  }

  lineItemTotalPages(): number {
    if (!this.listPageSize || !this.listTotalCount) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.listTotalCount / this.listPageSize));
  }

  lineItemHasPagination(): boolean {
    return this.lineItemTotalPages() > 1;
  }

  changeLineItemPage(page: number): void {
    if (!this.lineItemProducts || page < 1 || page > this.lineItemTotalPages() || page === this.listCurrentPage) {
      return;
    }
    this.lineItemProducts.currentPage = page;
    this.lineItemProducts.changePage();
  }

  lineItemPageRangeStart(): number {
    if (!this.listTotalCount) {
      return 0;
    }
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  lineItemPageRangeEnd(): number {
    if (!this.listTotalCount) {
      return 0;
    }
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }
  isTeamSelected(teamId: string): boolean {
    return this.hasTeam(teamId);
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  readonly compareById = compareById;

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allPublisherTeams.length) {
      return;
    }
    const teamId = ownerTeamId(this.editingModel);
    const match = _.find(this.allPublisherTeams, ['id', teamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }

  private updateCanUpdateProduct(): void {
    const teamId = ownerTeamId(this.editingModel);
    this.canUpdateProduct = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || (!!teamId && this.LocalUserPermissions.has('AX_PUBLISHER_TEAM_ADMIN', teamId));
  }
}
